// The country risk classification and the score it carries (SEP-228 §35).
//
// The register itself is rows in the database, because a FATF plenary changes
// the list roughly three times a year and a change must not need a deploy. This
// module holds the two things that are NOT data: the shape of a classification
// on the wire, and the pure function from one classification to one score.
//
// The AML questionnaire, the server controller and any PDF read this function,
// so the three surfaces cannot disagree. Ops selects a country. Nothing asks Ops
// whether a jurisdiction is FATF listed.
//
// See `vendor/septima-ontology-wealth/packages/ontology-wealth/specs/suitability/country-risk-classification.md`.

import { z } from 'zod';

/**
 * `grey` is the FATF list of jurisdictions under increased monitoring. `black`
 * is the list of high-risk jurisdictions subject to a call for action.
 */
export const countryFatfStatusSchema = z.enum(['none', 'grey', 'black']);
export type CountryFatfStatus = z.infer<typeof countryFatfStatusSchema>;

/** One classification of one country, from one day. */
export const countryRiskClassificationSchema = z.object({
    /** ISO 3166-1 alpha-2 — the same vocabulary `COUNTRIES` holds. */
    countryCode: z.string(),
    /** The en-GB country name, resolved for display. */
    countryName: z.string(),
    /** The country scores as the United Kingdom. */
    ukDomestic: z.boolean(),
    fatfStatus: countryFatfStatusSchema,
    /** The plain calendar day the classification takes effect. */
    effectiveFrom: z.string(),
    note: z.string().nullable(),
});
export type CountryRiskClassificationDto = z.infer<typeof countryRiskClassificationSchema>;

/** The score a classification carries. There is no fourth value. */
export type CountryRiskScore = 0 | 1 | 10;

/** The part of a classification the score reads. */
export interface CountryRiskScoreInput {
    readonly ukDomestic: boolean;
    readonly fatfStatus: CountryFatfStatus;
}

/**
 * The score one classification carries (SEP-228 §35).
 *
 * | Classification | Score |
 * |---|---|
 * | `fatfStatus` is `grey` or `black` | 10 |
 * | `ukDomestic` | 0 |
 * | Anything else | 1 |
 *
 * A grey or black status WINS over `ukDomestic`. The two never meet in practice,
 * and the higher score is the safe resolution of a register that says both.
 *
 * `null` means the register holds no row for the country. It scores 1, the same
 * as another non-UK country. The read names the missing row separately, so
 * Compliance sees the gap rather than a silent zero.
 */
export function countryRiskScore(
    classification: CountryRiskScoreInput | null | undefined,
): CountryRiskScore {
    if (!classification) return 1;
    if (classification.fatfStatus === 'grey' || classification.fatfStatus === 'black') return 10;
    if (classification.ukDomestic) return 0;
    return 1;
}

/** "United Kingdom", "FATF grey list", "Other country" — the score's reason. */
export function countryRiskReason(
    classification: CountryRiskScoreInput | null | undefined,
): string {
    if (!classification) return 'Not on the country risk register';
    if (classification.fatfStatus === 'black') return 'FATF call for action';
    if (classification.fatfStatus === 'grey') return 'FATF increased monitoring';
    if (classification.ukDomestic) return 'United Kingdom';
    return 'Other country';
}
